import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Mail, ArrowRight } from 'lucide-react';
interface LoginProps {
  onLogin: () => void;
}
export function Login({
  onLogin
}: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate authentication
    if (email && password) {
      onLogin();
    }
  };
  return <div className="min-h-screen flex bg-gray-50 font-sans" data-id="element-1355">
      {/* Left Side: Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-16 md:px-24 lg:px-32 relative z-10 bg-white shadow-[4px_0_24px_rgba(0,0,0,0.02)]" data-id="element-1356">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="w-full max-w-md mx-auto" data-id="element-1357">
          <div className="flex items-center gap-3 mb-12" data-id="element-1358">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center shadow-lg shadow-indigo-600/20" data-id="element-1359">
              <span className="text-white font-bold text-2xl" data-id="element-1360">N</span>
            </div>
            <span className="font-bold text-slate-900 text-2xl tracking-tight" data-id="element-1361">
              NiishEngine
            </span>
          </div>

          <div className="mb-10" data-id="element-1362">
            <h1 className="text-3xl font-bold text-slate-900 mb-3" data-id="element-1363">
              Welcome back
            </h1>
            <p className="text-gray-500 font-medium" data-id="element-1364">
              Log in to your partner dashboard to manage your white-label
              network.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1365">
            <div className="space-y-2" data-id="element-1366">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-1367">
                Email Address
              </label>
              <div className="relative" data-id="element-1368">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1369" />
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="partner@example.com" className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition-all font-medium text-slate-900" data-id="element-1370" />
              </div>
            </div>

            <div className="space-y-2" data-id="element-1371">
              <div className="flex items-center justify-between" data-id="element-1372">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-1373">
                  Password
                </label>
                <a href="#" className="text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors" data-id="element-1374">
                  Forgot Password?
                </a>
              </div>
              <div className="relative" data-id="element-1375">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1376" />
                <input type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition-all font-medium text-slate-900" data-id="element-1377" />
              </div>
            </div>

            <button type="submit" className="w-full py-3.5 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 transition-all flex items-center justify-center gap-2 group mt-8" data-id="element-1378">
              Sign In
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-1379" />
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-gray-500 font-medium" data-id="element-1380">
            Don't have a partner account?{' '}
            <a href="#" className="text-indigo-600 font-bold hover:underline" data-id="element-1381">
              Apply here
            </a>
          </p>
        </motion.div>
      </div>

      {/* Right Side: Branded Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden items-center justify-center" data-id="element-1382">
        {/* Abstract Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden" data-id="element-1383">
          <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-indigo-600/20 blur-[120px]" data-id="element-1384"></div>
          <div className="absolute bottom-[10%] -left-[10%] w-[50%] h-[50%] rounded-full bg-blue-500/20 blur-[100px]" data-id="element-1385"></div>
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.7,
        delay: 0.2
      }} className="relative z-10 max-w-lg px-12" data-id="element-1386">
          <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 p-8 rounded-2xl shadow-2xl" data-id="element-1387">
            <div className="flex gap-2 mb-6" data-id="element-1388">
              <div className="w-3 h-3 rounded-full bg-red-500/80" data-id="element-1389"></div>
              <div className="w-3 h-3 rounded-full bg-amber-500/80" data-id="element-1390"></div>
              <div className="w-3 h-3 rounded-full bg-emerald-500/80" data-id="element-1391"></div>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4 leading-tight" data-id="element-1392">
              The Enterprise OS for Hosting Resellers
            </h2>
            <p className="text-slate-400 leading-relaxed mb-8" data-id="element-1393">
              Manage multi-country operations, control your white-label
              branding, and track real-time profitability from a single,
              powerful dashboard.
            </p>

            <div className="space-y-4" data-id="element-1394">
              {[{
              label: 'Active Seats',
              value: '2,700+',
              color: 'text-emerald-400'
            }, {
              label: 'Global Uptime',
              value: '99.99%',
              color: 'text-indigo-400'
            }, {
              label: 'Monthly Volume',
              value: '$84k+',
              color: 'text-blue-400'
            }].map((stat, i) => <div key={i} className="flex items-center justify-between py-3 border-b border-slate-700/50 last:border-0" data-id="element-1395">
                  <span className="text-sm font-bold text-slate-300" data-id="element-1396">
                    {stat.label}
                  </span>
                  <span className={`text-lg font-bold ${stat.color}`} data-id="element-1397">
                    {stat.value}
                  </span>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}